import axios from "axios";

const BASE_URL = process.env.REACT_APP_BASE_URL || "http://localhost:3001";

/** Api Class
 *
 * Static class tying together the methods used to get/send to the API.
 */

class ValetApi {
    static token;

    // Public customer endpoints should not carry staff auth headers.
    static shouldAttachAuthHeader(endpoint = "") {
        const normalizedEndpoint = String(endpoint || "")
            .trim()
            .replace(/^\/+/, "")
            .toLowerCase();

        if (/^customers(?:[/?#]|$)/.test(normalizedEndpoint)) return false;
        if (/^customersurveys\/public(?:[/?#]|$)/.test(normalizedEndpoint)) return false;

        return true;
    }

    // Base request method to be used by all other methods for API calls
    // Takes in endpoint, data, and method
    // Returns a promise with the API response
    // Logs the API call and response for debugging
    // Handles errors and throws an array of error messages
    // *Note: The token is set in the class and used for authorization
    // *Note: The base URL is set in the environment variables or defaults to localhost
    // *Note: The method is set to "get" by default
    // *Note: The data is set to an empty object by default
    // *Note: The endpoint is set to the base URL and the endpoint passed in
    // *Note: The headers are set to include the token for authorization
    // *Note: The params are set to the data passed in if the method is "get"
    // *Note: The response is logged for debugging
    static async request(endpoint, data = {}, method = "get", timeout = 0) {
        const url = `${BASE_URL}/${endpoint}`;
        const shouldAttachAuth = ValetApi.shouldAttachAuthHeader(endpoint);
        const fallbackToken = shouldAttachAuth && typeof window !== "undefined" ? localStorage.getItem("token") : null;
        const authToken = shouldAttachAuth ? ValetApi.token || fallbackToken : null;
        const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
        const params = method === "get" ? data : {};
        try {
            const res = await axios({ url, method, data, params, headers, timeout });
            return res;
        } catch (err) {
            const message = err?.response?.data?.error?.message || err?.response?.data?.error || err?.message || "API request failed";
            const errors = Array.isArray(message) ? [...message] : [message];
            Object.defineProperties(errors, {
                status: { value: err?.response?.status || null },
                isUnavailable: { value: (!err?.response && Boolean(err?.request)) || [500, 502, 503, 504].includes(err?.response?.status) },
            });
            throw errors;
        }
    }

    //         -----AUTH METHODS-----
    static async validateUser(username, password) {
        const res = await this.request(`auth/token`, { username, password }, "post");
        return res;
    }

    static async validateTenantPortalUser(username, password) {
        const res = await this.request(`validations/tenant-auth/token`, { username, password }, "post");
        return res;
    }

    static async registerUser(username, password, firstName, lastName, email, phone, roleId, podiumId = null, totalParked = 0) {
        const res = await this.request(`auth/register`, { username, password, firstName, lastName, email, phone, roleId, podiumId, totalParked }, "POST");
        return res;
    }

    static async generateInvite(data) {
        const res = await this.request(`invites/`, data, "post");
        return res;
    }

    static async getInviteConfig() {
        const res = await this.request("invites/config");
        return res;
    }

    static async registerUserWithInvite(data) {
        const res = await this.request("auth/registerWithInvite", { ...data }, "post");
        return res;
    }

    static async forgotPassword(email) {
        const res = await this.request("auth/forgot-password", { email }, "post");
        return res;
    }

    static async resetPassword(token, password) {
        const res = await this.request("auth/reset-password", { token, password }, "post");
        return res;
    }

    static async forgotTenantPassword(username) {
        return await this.request("validations/tenant-auth/forgot-password", { username }, "post");
    }

    static async resetTenantPassword(token, password) {
        return await this.request("validations/tenant-auth/reset-password", { token, password }, "post");
    }

    //         -----ADMIN AUTH REGISTRATION APPROVAL METHODS-----
    static async getPendingUsersByPodiumId(podiumIds) {
        const res = await this.request(`users/pending`, { podiumIds }, "post");
        return res;
    }
    static async approveUser(userId) {
        const res = await this.request(`users/approve/${userId}`, {}, "patch");
        return res;
    }
    static async rejectUser(userId) {
        const res = await this.request(`users/reject/${userId}`, {}, "patch");
        return res;
    }

    //         -----CUSTOMER METHODS------
    static async getCustomerVehicle(ticketNum, customerToken) {
        const res = await this.request(`customers/ticket/${ticketNum}/${customerToken}`, { ticketNum, customerToken }, "get");
        return res;
    }
    static async customerRequestVehicle(ticketNum, customerToken) {
        const res = await this.request(`customers/request/${ticketNum}/${customerToken}`, { ticketNum, customerToken }, "post");
        return res;
    }
    static async customerGetVehiclePaymentStatus(ticketNum, customerToken) {
        const res = await this.request(`customers/payment/status/${ticketNum}/${customerToken}`);
        return res;
    }
    static async customerCreatePaymentIntent(ticketNum, customerToken) {
        const res = await this.request(`customers/payment/intent/${ticketNum}/${customerToken}`, {}, "post");
        return res;
    }
    static async customerSyncPaymentIntent(ticketNum, customerToken, paymentIntentId) {
        const res = await this.request(`customers/payment/intent/sync/${ticketNum}/${customerToken}`, { paymentIntentId }, "post");
        return res;
    }

    //         -----CUSTOMER SURVEY METHODS------
    // NEW UNTESTED METHOD 6/26/24
    static async getCustomerSurveyPublic(token) {
        const res = await this.request(`customerSurveys/public/${token}`);
        return res;
    }
    static async submitCustomerSurveyPublic(token, data) {
        const res = await this.request(`customerSurveys/public/${token}`, data, "post");
        return res;
    }
    static async createCustomerSurveyInvite(ticketNum, customerToken) {
        const res = await this.request(`customerSurveys/public/invite/${ticketNum}/${customerToken}`, {}, "post");
        return res;
    }

    //         -----USER METHODS------
    static async getAllUsers() {
        const res = await this.request(`users`);
        return res;
    }
    static async getUser(username) {
        const res = await this.request(`users/username/${username}`);
        return res;
    }
    static async getUserById(userId) {
        const res = await this.request(`users/id/${userId}`);
        return res;
    }
    static async getUsersByPodium(podiumId) {
        const res = await this.request(`users/podium/${podiumId}`);
        return res;
    }
    static async updateUser(username, data) {
        const res = await this.request(`users/${username}`, data, "patch");
        return res;
    }
    // * used NewAddVehicleForm to track total parked vehicles by user
    static async parkOne(userId) {
        const res = await this.request(`users/parkOne/${userId}`, {}, "patch");
        return res;
    }
    static async removeUserByUsername(username) {
        const res = await this.request(`users/${username}`, {}, "delete");
        return res;
    }
    static async removeUserById(id) {
        const res = await this.request(`users/remove/${id}`, {}, "delete");
        return res;
    }
    static async disableUser(userId) {
        const res = await this.request(`users/disable/${userId}`, {}, "patch");
        return res;
    }
    static async setUserAccess(userId, locationIds, podiumIds) {
        const res = await this.request(`users/id/${userId}/access`, { locationIds, podiumIds }, "put");
        return res;
    }

    static async getAppSettingsByUsername(username) {
        const res = await this.request(`users/username/${username}/app-settings`);
        return res;
    }

    static async saveAppSettingsByUsername(username, settings) {
        const res = await this.request(`users/username/${username}/app-settings`, settings, "put");
        return res;
    }

    //         -----ADMIN CONFIG USER METHODS------
    // !Admin Config
    static async setUserLocations(userId, locationIds) {
        const res = await this.request(`userLocations/userId/${userId}`, { locationIds }, "put");
        return res;
    }
    // ! Admin Config
    static async setUserPodiums(userId, podiumIds) {
        const res = await this.request(`userPodiums/userId/${userId}`, { podiumIds }, "put");
        return res;
    }

    //         -----VEHICLE METHODS-----
    //* used to create vehicle
    static async createVehicle(ticketNum, statusId, mobile, color, make, damages, notes) {
        const res = await this.request(`vehicles`, { ticketNum, statusId, mobile, color, make, damages, notes }, "post");
        return res;
    }
    static async getAllVehicles() {
        const res = await this.request(`vehicles`);
        return res;
    }
    static async getVehiclesByStatus(status) {
        const res = await this.request(`vehicles/status/${status}`);
        return res;
    }
    static async getVehicleByMobile(mobile) {
        let res;
        if (mobile === "") {
            res = await this.request(`vehicles/status/parked`);
        } else {
            res = await this.request(`vehicles/mobile/${mobile}`);
        }
        return res;
    }
    static async updateVehicle(id, data) {
        const res = await this.request(`vehicles/id/${id}`, data, "patch");
        return res;
    }
    static async deleteVehicle(id) {
        const res = await this.request(`vehicles/id/${id}`, {}, "delete");
        return res;
    }

    //           -----TRANSACTION METHODS-----
    // * used to create transaction with NEWADDVEHICLEFORM
    static async createTransaction(userId, vehicleId, podiumId, locationId, statusId, options = {}) {
        const payload = { userId, vehicleId, podiumId, locationId, statusId };
        if (typeof options?.parkingSpot === "string") {
            payload.parkingSpot = options.parkingSpot.trim() || null;
        }
        const res = await this.request(`transactions`, payload, "post");
        return res;
    }
    static async getAllTransactionsByLocationAndStatus(locationId, status) {
        const res = await this.request(`transactions/location/${locationId}/status/${status}`);
        return res;
    }
    static async getAllTransactionsByLocationAndUserId(locationId, userId) {
        const res = await this.request(`transactions/location/${locationId}/user/${userId}`);
        return res;
    }
    static async getAllTransactionsByDateRange(startYear, startMonth, startDay, endYear, endMonth, endDay) {
        const res = await this.request(`transactions/range`, { startYear, startMonth, startDay, endYear, endMonth, endDay });
        return res;
    }
    static async getTodayTransactionsByLocationId(locationId) {
        const res = await this.request(`transactions/today`, { locationId });
        return res;
    }
    // ! Old version no longer working
    // static async getActiveTransactionsByMobile(locationId, mobile) {
    //     const res = await this.request(`transactions/search/location/${locationId}/mobile/${mobile}`);
    //     return res;
    // }
    // ! OLD LOST KEYS
    static async lostKeys(userId) {
        const res = await this.request(`data/lostKeys/userId/${userId}`);
        return res;
    }
    // * NEW LOST KEYS
    static async lostKeysNew(userId, transactionId) {
        const res = await this.request(`data/lostKeys/userId/${userId}/transactionId/${transactionId}`);
        return res;
    }

    static async getTransactionById(id) {
        const res = await this.request(`transactions/id/${id}`);
        return res;
    }
    static async updateTransactionById(id, data) {
        const res = await this.request(`transactions/${id}`, data, "patch");
        return res;
    }
    static async initiateTransfer(vehicleId, fromPodiumId, toPodiumId, note = "") {
        const res = await this.request(`transactions/transfers/initiate`, { vehicleId, fromPodiumId, toPodiumId, note }, "post");
        return res;
    }
    static async initiateTransferAll(fromPodiumId, toPodiumId, note = "") {
        const res = await this.request(`transactions/transfers/initiate-all`, { fromPodiumId, toPodiumId, note }, "post");
        return res;
    }
    static async getIncomingTransfersByPodium(podiumId, status = "pending") {
        const res = await this.request(`transactions/transfers/incoming/podium/${podiumId}`, { status }, "get");
        return res;
    }
    static async acceptTransfer(transferId, note = "") {
        const res = await this.request(`transactions/transfers/${transferId}/accept`, { note }, "patch");
        return res;
    }
    static async rejectTransfer(transferId, note = "") {
        const res = await this.request(`transactions/transfers/${transferId}/reject`, { note }, "patch");
        return res;
    }
    static async cancelTransfer(transferId, note = "") {
        const res = await this.request(`transactions/transfers/${transferId}/cancel`, { note }, "patch");
        return res;
    }
    static async deleteTransaction(id) {
        const res = await this.request(`/transactions/${id}`);
        return res;
    }

    static async saveTransactionDamages(transactionId, marks = [], notes = "", photoFiles = [], options = {}) {
        const normalizedMarks = Array.isArray(marks) ? marks : [];
        const normalizedNotes = String(notes || "");
        const normalizedFiles = Array.isArray(photoFiles) ? photoFiles.filter(Boolean) : [];
        const responseMode =
            String(options?.responseMode || "")
                .trim()
                .toLowerCase() === "minimal"
                ? "minimal"
                : "full";
        const endpoint = `transactions/${transactionId}/damages${responseMode === "minimal" ? "?responseMode=minimal" : ""}`;

        if (normalizedFiles.length === 0) {
            const res = await this.request(endpoint, { marks: normalizedMarks, notes: normalizedNotes }, "post");
            return res;
        }

        const formData = new FormData();
        formData.append("marks", JSON.stringify(normalizedMarks));
        formData.append("notes", normalizedNotes);

        normalizedFiles.forEach((file) => {
            formData.append("photos", file);
        });

        const res = await this.request(endpoint, formData, "post");
        return res;
    }

    static async getTransactionDamages(transactionId) {
        const res = await this.request(`transactions/${transactionId}/damages`);
        return res;
    }

    static async replaceTransactionDamagePhoto(transactionId, photoId, photoFile) {
        const formData = new FormData();
        formData.append("photo", photoFile);

        const res = await this.request(`transactions/${transactionId}/damages/photos/${photoId}/replace`, formData, "post");
        return res;
    }

    static async getTransactionDamagesReport(transactionId) {
        const res = await this.request(`transactions/${transactionId}/damages/report`);
        return res;
    }

    //         -----LOCATION METHODS-----
    static async createLocation(values) {
        const res = await this.request(`locations`, { ...values }, "post");
        return res;
    }
    static async getAllLocations() {
        const res = await this.request(`locations/`);
        return res;
    }
    static async getLocationById(id) {
        const res = await this.request(`locations/id/${id}`);
        return res;
    }
    static async getLocationBySitename(sitename) {
        const res = await this.request(`locations/sitename/${sitename}`);
        return res;
    }

    // ! UserConfig > UserManagement
    static async getLocationsByRegion(regionId) {
        const res = await this.request(`locations/region/${regionId}`);
        return res;
    }
    static async updateLocation(id, data) {
        const res = await this.request(`locations/${id}`, data, "patch");
        return res;
    }
    static async removeLocation(id) {
        const res = await this.request(`locaations/${id}`, {}, "delete");
        return res;
    }

    //        -----PODIUM METHODS-----
    static async createPodium(name, locationId, isDeleted = false, parkingCapacity = null) {
        const res = await this.request(`podiums`, { name, locationId, isDeleted, parkingCapacity }, "post");
        return res;
    }
    static async updatePodium(id, data) {
        const res = await this.request(`podiums/id/${id}`, data, "patch");
        return res;
    }
    static async getAllPodiums() {
        const res = await this.request(`podiums`);
        return res;
    }
    static async getPodiumById(id) {
        const res = await this.request(`podiums/id/${id}`);
        return res;
    }
    // ! UserConfig > UserManagement
    static async getPodiumsByLocation(locationId) {
        const res = await this.request(`podiums/location/${locationId}`);
        return res;
    }

    //       -----ROLE METHODS-----
    static async getAllRoles() {
        const res = await this.request("roles");
        return res;
    }

    //       -----REGION METHODS-----
    static async getAllRegions() {
        const res = await this.request("regions");
        return res;
    }
    static async createRegion(name) {
        const res = await this.request("regions", { name }, "post");
        return res;
    }
    //       -----DATA METHODS-----
    static async getPodiumDailyKpis(podiumId, userTimeZone) {
        const res = await this.request(`data/podium/${podiumId}/dailyKpis`, { timezone: userTimeZone });
        return res;
    }

    static async getGarageDataByPodiumId(podiumId) {
        const res = await this.request(`data/garageData/${podiumId}`);
        return res;
    }
    static async getGarageDataActiveByPodiumId(podiumId) {
        const res = await this.request(`data/garageData/active/${podiumId}`);
        return res;
    }
    static async getGarageCardsByPodiumId(podiumId, userTimeZone = "UTC", date = null, view = "operational", sortBy = "default") {
        const query = { timezone: userTimeZone, view, sortBy };
        if (date) query.date = date;
        const res = await this.request(`data/garageData/cards/podium/${podiumId}`, query, "get", 10000);
        return res;
    }
    static async getTransactionDetailById(transactionId) {
        const res = await this.request(`data/transactionDetail/${transactionId}`);
        return res;
    }
    static async getPodiumsByLocationId(locationId) {
        const res = await this.request(`data/podiums/locationId/${locationId}`);
        return res;
    }

    //       -----DATA METHODS----- Valet Dashboard
    // ! ValetDashBoard showing Today's parked retrieved activity
    static async getTodayValetActivityByPodiumId(podiumId, userTimeZone = "UTC", date = null) {
        const query = { timezone: userTimeZone };
        if (date) query.date = date;
        const res = await this.request(`data/todayUserTransactionCount/podiumId/${podiumId}`, query);
        return res;
    }

    static async getValetActivityByPodiumIdDateRange(podiumId, userTimeZone = "UTC", startDate = null, endDate = null) {
        const query = { timezone: userTimeZone };
        if (startDate) query.start = startDate;
        if (endDate) query.end = endDate;
        const res = await this.request(`data/userTransactionCount/podiumId/${podiumId}`, query);
        return res;
    }
    // ! ValetDashBoard showing Garage total
    // static async getGarageCountByPodiumId(podiumId) {
    //     const res = await this.request(`data/garageCount/podiumId/${podiumId}`);
    //     return res;
    // }
    //! ValetDashBoard Queue Data
    static async getRequestedDataByPodiumId(podiumId) {
        const res = await this.request(`data/todayRequestedTransactionsData/podiumId/${podiumId}`);
        return res;
    }

    // ! ADMIN Dashboard
    static async getGarageSnapshotByPodiumId(podiumId, userTimeZone, date = null) {
        const query = { timezone: userTimeZone };
        if (date) query.date = date;
        const res = await this.request(`data/garageData/snapshot/podium/${podiumId}`, query);
        return res;
    }

    static async getGarageSnapshotsByLocationId(locationId, userTimeZone, date = null, startDate = null, endDate = null) {
        const query = { timezone: userTimeZone };
        if (date) query.date = date;
        if (startDate) query.startDate = startDate;
        if (endDate) query.endDate = endDate;
        const res = await this.request(`data/garageData/snapshot/location/${locationId}`, query);
        return res;
    }

    static async getPodiumHourlyFlowByPodiumId(podiumId, userTimeZone = "UTC", date = null) {
        const query = { timezone: userTimeZone };
        if (date) query.date = date;
        const res = await this.request(`data/garageData/hourlyFlow/podium/${podiumId}`, query);
        return res;
    }

    static async getPodiumHourlyFlowByPodiumDateRange(podiumId, userTimeZone = "UTC", startDate = null, endDate = null) {
        const query = { timezone: userTimeZone };
        if (startDate) query.startDate = startDate;
        if (endDate) query.endDate = endDate;
        const res = await this.request(`data/garageData/hourlyFlowRange/podium/${podiumId}`, query);
        return res;
    }

    static async getArchiveHealth() {
        const res = await this.request("dashboard/archive/health");
        return res;
    }

    // !ADMIN Dashboard
    // `YYYY-MM-DD` format for startDate and endDate
    static async getAllByPodiumIdDateRange(podiumId, startDate, endDate) {
        const res = await this.request(`dashboard/transactions/podiumId/${podiumId}/date/${startDate}/${endDate}`);
        return res;
    }
    // ! useGarageSearchHandler
    static async getByPodiumMobile(podiumId, mobile) {
        const res = await this.request(`data/byPodiumMobile/${podiumId}/${mobile}`, {}, "get");
        return res;
    }
    // !GarageDetail
    static async getLatestTransactionDetailByVehicleId(vehicleId, podiumId = null) {
        const query = {};
        if (podiumId) query.podiumId = podiumId;
        const res = await this.request(`data/latestTransactionDetail/vehicleId/${vehicleId}`, query);
        return res;
    }
    //       -----DATA METHODS-----
    // ! GarageDetail
    static async getTransactionHistoryByVehicleId(vehicleId) {
        const res = await this.request(`data/transactionHistory/vehicleId/${vehicleId}`);
        return res;
    }
    // ! Admin Report
    static async getSurveyReportByPodiumDateRange(podiumId, startDate, endDate, userTimeZone = "UTC") {
        const res = await this.request(`surveys/report/podiumId/${podiumId}/date/${startDate}/${endDate}`, { timezone: userTimeZone });
        return res;
    }

    //       -----USER PODIUM METHODS-----
    // ! UserContext
    static async getUserPodiums(userId) {
        const res = await this.request(`userPodiums/userId/${userId}`);
        return res;
    }
    // ! UserContext
    //       -----USER LOCATION METHODS-----
    static async getUserLocations(userId) {
        const res = await this.request(`userLocations/userId/${userId}`);
        return res;
    }

    static async getPushPublicKey() {
        const res = await this.request(`notifications/public-key`);
        return res;
    }

    static async subscribePushNotifications({ subscription, locationId = null, podiumId = null, deviceLabel = "" }) {
        const res = await this.request(
            `notifications/subscribe`,
            {
                subscription,
                locationId,
                podiumId,
                deviceLabel,
            },
            "post",
        );
        return res;
    }

    static async unsubscribePushNotifications(endpoint) {
        const res = await this.request(`notifications/unsubscribe`, { endpoint }, "post");
        return res;
    }

    //       -----PAYMENT METHODS-----
    static async getPaymentsFeatureStatus() {
        const res = await this.request(`payments/feature`);
        return res;
    }

    static async getPaymentConfigByPodium(podiumId) {
        const res = await this.request(`payments/config/podium/${podiumId}`);
        return res;
    }

    static async upsertPaymentConfigByPodium(podiumId, config) {
        const res = await this.request(`payments/config/podium/${podiumId}`, config, "put");
        return res;
    }

    static async getVehiclePaymentStatus(vehicleId, podiumId = null) {
        const query = {};
        if (podiumId) query.podiumId = Number(podiumId);
        const res = await this.request(`payments/status/vehicle/${vehicleId}`, query, "get", 10000);
        return res;
    }

    static async createVehiclePaymentIntent(vehicleId, podiumId) {
        const res = await this.request(`payments/intent/vehicle/${vehicleId}`, { podiumId: Number(podiumId) }, "post");
        return res;
    }

    static async syncPaymentIntent(paymentIntentId) {
        const res = await this.request(`payments/intent/sync`, { paymentIntentId }, "post");
        return res;
    }

    static async settleVehiclePaymentInPerson(vehicleId, podiumId, data = {}) {
        const payload = {
            podiumId: Number(podiumId),
            method: data.method || "cash",
            note: data.note || "",
            amountPaidCents: Number.isInteger(Number(data.amountPaidCents)) ? Number(data.amountPaidCents) : null,
        };
        const res = await this.request(`payments/manual/vehicle/${vehicleId}`, payload, "post");
        return res;
    }

    static async getPaymentLocationRoutings(includeInactive = false) {
        const query = includeInactive ? { includeInactive: true } : {};
        const res = await this.request("payments/routing/location", query);
        return res;
    }

    static async getPaymentLocationRouting(locationId) {
        const res = await this.request(`payments/routing/location/${locationId}`);
        return res;
    }

    static async upsertPaymentLocationRouting(locationId, payload) {
        const res = await this.request(`payments/routing/location/${locationId}`, payload, "put");
        return res;
    }

    static async createPaymentConnectAccount(locationId, payload = {}) {
        const res = await this.request(`payments/connect/location/${locationId}/account`, payload, "post");
        return res;
    }

    static async createPaymentConnectOnboardingLink(locationId, payload = {}) {
        const res = await this.request(`payments/connect/location/${locationId}/onboarding-link`, payload, "post");
        return res;
    }

    static async getPaymentConnectStatus(locationId) {
        const res = await this.request(`payments/connect/location/${locationId}/status`);
        return res;
    }

    static async getPaymentReadersByLocation(locationId, includeInactive = false) {
        const query = includeInactive ? { includeInactive: true } : {};
        const res = await this.request(`payments/routing/readers/location/${locationId}`, query);
        return res;
    }

    static async upsertPaymentTerminalReader(stripeReaderId, payload) {
        const res = await this.request(`payments/routing/readers/${stripeReaderId}`, payload, "put");
        return res;
    }

    static async getPaymentRevenueReport({ locationId = null, startDate = null, endDate = null, timeZone = null } = {}) {
        const query = {};
        if (locationId) query.locationId = Number(locationId);
        if (startDate) query.startDate = startDate;
        if (endDate) query.endDate = endDate;
        if (timeZone) query.timeZone = timeZone;

        const res = await this.request("payments/report/revenue", query);
        return res;
    }

    static async listValidationTenantsByLocation(locationId, includeInactive = false) {
        const query = includeInactive ? { includeInactive: true } : {};
        const res = await this.request(`validations/tenants/location/${locationId}`, query);
        return res;
    }

    static async createValidationTenant(locationId, payload) {
        const res = await this.request(`validations/tenants/location/${locationId}`, payload, "post");
        return res;
    }

    static async createValidationPrintBatch(tenantId, payload) {
        const res = await this.request(`validations/batches/tenant/${tenantId}`, payload, "post");
        return res;
    }

    static async getValidationBatch(batchId) {
        const res = await this.request(`validations/batches/${batchId}`);
        return res;
    }

    static async lookupValidationVisit(payload) {
        const res = await this.request("validations/lookup", payload, "post");
        return res;
    }

    static async lookupTenantValidationVisit(payload) {
        const res = await this.request("validations/tenant/lookup", payload, "post");
        return res;
    }

    static async redeemValidationToken(payload) {
        const res = await this.request("validations/redeem", payload, "post");
        return res;
    }

    static async redeemTenantValidationToken(payload) {
        const res = await this.request("validations/tenant/redeem", payload, "post");
        return res;
    }

    static async getTenantValidationProfile() {
        const res = await this.request("validations/tenant/me");
        return res;
    }

    static async createTenantValidationPrintBatch(payload) {
        const res = await this.request("validations/tenant/batches", payload, "post");
        return res;
    }

    static async getTenantValidationUsage({ range = "month", startDate = null, endDate = null } = {}) {
        const query = { range };
        if (startDate) query.startDate = startDate;
        if (endDate) query.endDate = endDate;
        const res = await this.request("validations/tenant/usage", query);
        return res;
    }

    static async getTenantValidationMonthlyInvoice(month) {
        const res = await this.request(`validations/tenant/invoice/month/${month}`);
        return res;
    }

    static async getTenantValidationRangeInvoice({ startDate, endDate }) {
        const query = {};
        if (startDate) query.startDate = startDate;
        if (endDate) query.endDate = endDate;
        const res = await this.request("validations/tenant/invoice/range", query);
        return res;
    }

    static async getValidationLocationReport(locationId, { startDate = null, endDate = null } = {}) {
        const query = {};
        if (startDate) query.startDate = startDate;
        if (endDate) query.endDate = endDate;
        const res = await this.request(`validations/report/location/${locationId}`, query);
        return res;
    }

    static async getValidationTenantMonthlyInvoice(tenantId, month, { podiumId = null } = {}) {
        const query = {};
        if (podiumId) query.podiumId = Number(podiumId);
        const res = await this.request(`validations/invoice/tenant/${tenantId}/month/${month}`, query);
        return res;
    }

    static async getValidationTenantRangeInvoice(tenantId, { startDate, endDate, podiumId = null }) {
        const query = {};
        if (startDate) query.startDate = startDate;
        if (endDate) query.endDate = endDate;
        if (podiumId) query.podiumId = Number(podiumId);
        const res = await this.request(`validations/invoice/tenant/${tenantId}/range`, query);
        return res;
    }

    static async getValidationBulkMonthlyInvoices({ month, scopeType, scopeId }) {
        const query = {
            scopeType,
            scopeId: Number(scopeId),
        };
        const res = await this.request(`validations/invoice/bulk/month/${month}`, query);
        return res;
    }

    static async getValidationBulkRangeInvoices({ startDate, endDate, scopeType, scopeId }) {
        const query = {
            scopeType,
            scopeId: Number(scopeId),
        };
        if (startDate) query.startDate = startDate;
        if (endDate) query.endDate = endDate;
        const res = await this.request("validations/invoice/bulk/range", query);
        return res;
    }

    static async emailValidationTenantMonthlyInvoice(tenantId, month, payload = {}) {
        const res = await this.request(`validations/invoice/tenant/${tenantId}/month/${month}/email`, payload, "post");
        return res;
    }
}

// ? TODO DELETE ValetApi.token
// *token needed for interacting with API, this is here for development.
// ValetApi.token = `eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VybmFtZSI6ImVkZHltdW5zdGEiLCJpc0FkbWluIjp0cnVlLCJpYXQiOjE3MTIyNDE2NjB9.Gun34muN6saafHRlJa4C7ep-r2FiuctfiC05iIqaXJ8`;

export default ValetApi;
